import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { MODULE_THEMES } from "../data/modules";
import { Heart, Flame, Wind, Sunrise, Scale, Target, ArrowRight, Sparkles, Puzzle, Shield, Repeat, Sprout, Compass, Waves, UserCog, Feather, Star, Anchor, Apple, Handshake, HeartHandshake, Ear, Megaphone, MessagesSquare, LifeBuoy } from "lucide-react";

const ICONS = { Heart, Flame, Wind, Sunrise, Scale, Target, Puzzle, Shield, Repeat, Sprout, Compass, Waves, UserCog, Feather, Star, Anchor, Apple, Handshake, HeartHandshake, Ear, Megaphone, MessagesSquare, LifeBuoy };

export default function Hub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api
      .get("/modules")
      .then((r) => setModules(r.data))
      .finally(() => setLoading(false));
    api.get("/goals").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="animate-fade-up mb-10">
          <p className="text-muted-foreground font-semibold">Hello, {firstName} 👋</p>
          <h1 className="font-head font-extrabold text-3xl sm:text-4xl mt-1 tracking-tight">
            Choose a program to explore
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Each program is its own guided space. Answer the interventions, learn as you go, and
            collect rewards for your progress.
          </p>
        </div>

        {user && user.role !== "admin" && !user.subscription_active && (
          <button
            onClick={() => navigate("/subscribe")}
            className="w-full text-left rounded-3xl p-5 mb-5 border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all animate-fade-up flex items-center justify-between gap-4"
            style={{ backgroundColor: "#FFFBEB", borderColor: "rgba(246,224,94,0.4)" }}
            data-testid="hub-upgrade-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#D4AF37] flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-head font-bold text-[15px]">
                  {Math.max(0, 5 - (user.free_answers_used || 0))} free answers left
                </p>
                <p className="text-sm text-muted-foreground">
                  Subscribe for $3/month to unlock unlimited access to every program.
                </p>
              </div>
            </div>
            <span className="text-sm font-semibold text-[#B45309] whitespace-nowrap">Upgrade →</span>
          </button>
        )}

        {plan && (
          <button
            onClick={() => navigate("/plan")}
            className="w-full text-left rounded-3xl p-6 mb-8 border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all animate-fade-up flex items-center justify-between gap-4"
            style={{ backgroundColor: "#FCF3EB" }}
            data-testid="hub-plan-banner"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#E8A87C] flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-serif-accent text-[15px] text-[#2D3748] leading-relaxed max-w-xl">
                  "{plan.affirmation}"
                </p>
                <p className="text-sm font-semibold text-[#B45309] mt-2">
                  Weekly goals: {plan.goals_completed}/{plan.goals_total} complete · View your plan
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#B45309] shrink-0" />
          </button>
        )}

        {loading ? (
          <p className="text-muted-foreground">Loading programs…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules.map((m, i) => {
              const t = MODULE_THEMES[m.key];
              const Icon = ICONS[t.icon];
              const pct = m.question_count ? Math.round((m.correct / m.question_count) * 100) : 0;
              return (
                <button
                  key={m.key}
                  onClick={() => navigate(`/module/${m.key}`)}
                  className="text-left rounded-3xl p-7 border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all animate-fade-up group"
                  style={{ backgroundColor: t.bg, animationDelay: `${i * 0.05}s` }}
                  data-testid={`module-card-${m.key}`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: t.primary }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <ArrowRight
                      className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                      style={{ color: t.primary }}
                    />
                  </div>
                  <h3 className="font-head font-bold text-lg mt-5 leading-tight">{m.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{m.tagline}</p>

                  <div className="mt-5">
                    <div className="flex justify-between text-xs font-semibold mb-1.5">
                      <span style={{ color: t.primary }}>
                        {m.correct}/{m.question_count} mastered
                      </span>
                      <span className="text-muted-foreground">{pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-black/5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: t.primary }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
